import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowClockwise, XCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const COPY = {
  en: {
    restart: "Restart Interview",
    end: "End Interview",
    firstBody: "Start this interview again from Question 1. Your current answers and progress will be permanently deleted. This complimentary restart will not use another interview session.",
    paidBody: "You’ve already used your complimentary restart for this interview. Continuing will permanently delete your current answers and progress and use 1 interview session from your daily allowance.",
    noSessions: "No interview sessions remaining today",
    cancel: "Cancel",
    firstCta: "Restart Interview",
    paidCta: "Restart and use 1 session",
    endBody: "End this interview and permanently delete your current answers and progress. This interview session will not be restored.",
    endCta: "End Interview",
    actionError: "Could not update the interview. Please try again.",
  },
  "pt-BR": {
    restart: "Reiniciar entrevista",
    end: "Encerrar entrevista",
    firstBody: "Comece esta entrevista novamente a partir da Pergunta 1. Suas respostas e seu progresso atuais serão excluídos permanentemente. Esta reinicialização de cortesia não usará outra sessão de entrevista.",
    paidBody: "Você já usou a reinicialização de cortesia desta entrevista. Continuar excluirá permanentemente suas respostas e seu progresso atuais e usará 1 sessão de entrevista do seu limite diário.",
    noSessions: "Nenhuma sessão de entrevista restante hoje",
    cancel: "Cancelar",
    firstCta: "Reiniciar entrevista",
    paidCta: "Reiniciar e usar 1 sessão",
    endBody: "Encerre esta entrevista e exclua permanentemente suas respostas e seu progresso atuais. Esta sessão de entrevista não será restaurada.",
    endCta: "Encerrar entrevista",
    actionError: "Não foi possível atualizar a entrevista. Tente novamente.",
  },
  "es-419": {
    restart: "Reiniciar entrevista",
    end: "Finalizar entrevista",
    firstBody: "Comience esta entrevista nuevamente desde la Pregunta 1. Sus respuestas y progreso actuales se eliminarán permanentemente. Este reinicio de cortesía no utilizará otra sesión de entrevista.",
    paidBody: "Ya utilizó el reinicio de cortesía de esta entrevista. Si continúa, se eliminarán permanentemente sus respuestas y progreso actuales y se utilizará 1 sesión de entrevista de su asignación diaria.",
    noSessions: "No quedan sesiones de entrevista hoy",
    cancel: "Cancelar",
    firstCta: "Reiniciar entrevista",
    paidCta: "Reiniciar y usar 1 sesión",
    endBody: "Finalice esta entrevista y elimine permanentemente sus respuestas y progreso actuales. Esta sesión de entrevista no se restaurará.",
    endCta: "Finalizar entrevista",
    actionError: "No se pudo actualizar la entrevista. Inténtelo de nuevo.",
  },
  "fr-CA": {
    restart: "Recommencer l’entrevue",
    end: "Mettre fin à l’entrevue",
    firstBody: "Recommencez cette entrevue à la question 1. Vos réponses et votre progression actuelles seront supprimées définitivement. Ce redémarrage gratuit n’utilisera pas une autre session d’entrevue.",
    paidBody: "Vous avez déjà utilisé le redémarrage gratuit de cette entrevue. Continuer supprimera définitivement vos réponses et votre progression actuelles et utilisera 1 session d’entrevue de votre allocation quotidienne.",
    noSessions: "Aucune session d’entrevue restante aujourd’hui",
    cancel: "Annuler",
    firstCta: "Recommencer l’entrevue",
    paidCta: "Recommencer et utiliser 1 session",
    endBody: "Mettez fin à cette entrevue et supprimez définitivement vos réponses et votre progression actuelles. Cette session d’entrevue ne sera pas rétablie.",
    endCta: "Mettre fin à l’entrevue",
    actionError: "Impossible de mettre à jour l’entrevue. Veuillez réessayer.",
  },
};

function copyFor(lang) {
  const raw = String(lang || "en").toLowerCase();
  if (raw.startsWith("pt")) return COPY["pt-BR"];
  if (raw.startsWith("es")) return COPY["es-419"];
  if (raw.startsWith("fr")) return COPY["fr-CA"];
  return COPY.en;
}

function ConfirmModal({ title, body, cancel, confirm, danger = false, busy, onCancel, onConfirm }) {
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-6" onClick={() => !busy && onCancel()}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
        <p className="mt-4 text-sm leading-relaxed text-[color:var(--text-secondary)]">{body}</p>
        <div className="mt-7 flex justify-end gap-3">
          <button type="button" disabled={busy} onClick={onCancel} className="rounded-full border border-[color:var(--border-stone)] px-5 py-2.5 text-sm font-medium disabled:opacity-50">{cancel}</button>
          <button type="button" disabled={busy} onClick={onConfirm} className={`rounded-full px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50 ${danger ? "bg-[color:var(--score-bad)]" : "bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)]"}`}>{confirm}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function InterviewLifecycleControls() {
  const location = useLocation();
  const navigate = useNavigate();
  const match = location.pathname.match(/^\/interview\/([^/]+)$/);
  const sessionId = match?.[1] || null;
  const [target, setTarget] = useState(null);
  const [session, setSession] = useState(null);
  const [allowance, setAllowance] = useState(null);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setTarget(null);
      return undefined;
    }
    let observer;
    const attach = () => {
      const legacy = document.querySelector('[data-testid="restart-interview-btn"]');
      if (!legacy || !legacy.parentElement) return false;
      legacy.style.display = "none";
      setTarget(legacy.parentElement);
      return true;
    };
    if (!attach()) {
      observer = new MutationObserver(() => {
        if (attach() && observer) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
    return () => {
      if (observer) observer.disconnect();
      const legacy = document.querySelector('[data-testid="restart-interview-btn"]');
      if (legacy) legacy.style.display = "";
    };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    Promise.all([
      api.get(`/sessions/${sessionId}`),
      api.get("/session-allowance"),
    ]).then(([sessionRes, allowanceRes]) => {
      if (cancelled) return;
      setSession(sessionRes.data);
      setAllowance(allowanceRes.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId]);

  const c = useMemo(() => copyFor(session?.interface_language || session?.language), [session]);
  const isFreeTrial = Boolean(session?.free_trial);
  const restartUsed = Number(session?.restart_count || 0) > 0;
  const hasPaidRestart = Boolean(allowance?.limit_disabled || (Number(allowance?.remaining || 0) > 0));
  const restartDisabled = restartUsed && allowance && !hasPaidRestart;

  const restart = async () => {
    if (!sessionId || busy || isFreeTrial) return;
    setBusy(true);
    window.__MEC_RESTARTING_SESSION__ = sessionId;
    try {
      await api.post(`/sessions/${sessionId}/restart-interview`);
      window.location.reload();
    } catch (err) {
      delete window.__MEC_RESTARTING_SESSION__;
      const message = err?.response?.data?.detail?.message || err?.response?.data?.detail || c.actionError;
      toast.error(typeof message === "string" ? message : c.actionError);
      setBusy(false);
      setModal(null);
      try {
        const { data } = await api.get("/session-allowance");
        setAllowance(data);
      } catch { /* ignore refresh failure */ }
    }
  };

  const endInterview = async () => {
    if (!sessionId || busy) return;
    setBusy(true);
    window.__MEC_ENDING_SESSION__ = sessionId;
    try {
      await api.post(`/sessions/${sessionId}/end-interview`);
      try { window.sessionStorage.removeItem(`mec_warmup_${sessionId}`); } catch { /* noop */ }
      navigate("/");
    } catch (err) {
      delete window.__MEC_ENDING_SESSION__;
      toast.error(err?.response?.data?.detail || c.actionError);
      setBusy(false);
      setModal(null);
    }
  };

  if (!sessionId || !target) return null;

  const controls = (
    <>
      {!isFreeTrial && (
        <div className="flex flex-col items-center">
          <button
            type="button"
            data-testid="restart-interview-lifecycle-btn"
            disabled={Boolean(restartDisabled)}
            onClick={() => !restartDisabled && setModal("restart")}
            className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-surface-hover)] hover:text-[color:var(--text-primary)] px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowClockwise size={16} /> {c.restart}
          </button>
          {restartDisabled && <span className="mt-1 max-w-40 text-center text-[10px] leading-tight text-[color:var(--text-secondary)]">{c.noSessions}</span>}
        </div>
      )}
      <button type="button" data-testid="end-interview-btn" onClick={() => setModal("end")} className="inline-flex items-center gap-2 rounded-full border border-[color:var(--score-bad)]/40 text-[color:var(--score-bad)] hover:bg-[color:var(--score-bad)]/5 px-4 py-2 text-sm">
        <XCircle size={16} /> {c.end}
      </button>
    </>
  );

  return (
    <>
      {createPortal(controls, target)}
      {modal === "restart" && !isFreeTrial && (
        <ConfirmModal title={c.restart} body={restartUsed ? c.paidBody : c.firstBody} cancel={c.cancel} confirm={restartUsed ? c.paidCta : c.firstCta} busy={busy} onCancel={() => setModal(null)} onConfirm={restart} />
      )}
      {modal === "end" && (
        <ConfirmModal title={c.end} body={c.endBody} cancel={c.cancel} confirm={c.endCta} danger busy={busy} onCancel={() => setModal(null)} onConfirm={endInterview} />
      )}
    </>
  );
}
